// Employment Service
// API service layer — data comes from the real API.
// Returns empty arrays/objects when an endpoint is unavailable (404/network).

import axios from "axios";
import axiosInstance from "../../../../common/axiosInstance";
import { defaultEmploymentFilters, defaultPagination } from "../constant/data";

const LIVE_API_BASE = "https://teamsapi.autoappy.com";

// ─── Helpers ────────────────────────────────────────────────────────────────

const extractArray = (res) =>
  Array.isArray(res?.data?.data)
    ? res.data.data
    : Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res?.data?.results)
        ? res.data.results
        : Array.isArray(res?.data?.items)
          ? res.data.items
          : [];

/** Silently swallow 404s / network errors and return a default value */
const safe = async (fn, fallback) => {
  try {
    return await fn();
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404 || status === 405) {
      console.info(`[employmentService] ${status} — endpoint not available yet`);
    } else {
      console.warn("[employmentService] API error:", err.message);
    }
    return fallback;
  }
};

const deriveDepartment = (designation, remarks) => {
  const text = `${designation || ""} ${remarks || ""}`.toLowerCase();
  if (text.includes("developer") || text.includes("engineer") || text.includes("devops") || text.includes("qa")) {
    return "Engineering";
  }
  if (text.includes("designer") || text.includes("ux") || text.includes("ui")) {
    return "Design";
  }
  if (text.includes("hr") || text.includes("recruiter") || text.includes("human")) {
    return "Human Resources";
  }
  if (text.includes("manager") || text.includes("lead")) {
    return "Management";
  }
  if (text.includes("analyst") || text.includes("business")) {
    return "Business Operations";
  }
  return "Operations";
};

const deriveEmploymentType = (designation, remarks) => {
  const text = `${designation || ""} ${remarks || ""}`.toLowerCase();
  if (text.includes("intern")) return "Internship";
  if (text.includes("contract")) return "Fixed-Term Contract";
  if (text.includes("part")) return "Part-Time";
  return "Full-Time Permanent";
};

const deriveContractType = (designation, remarks) => {
  const text = `${designation || ""} ${remarks || ""}`.toLowerCase();
  if (text.includes("intern")) return "Internship Agreement";
  if (text.includes("contract")) return "Fixed-Term Contract";
  return "Permanent Agreement";
};

const DIVERSE_FALLBACKS = {
  "EMP-0001": {
    department_name: "Engineering",
    designation: "Software Developer",
    employment_type_name: "Internship",
    contract_type: "Internship Agreement",
    work_schedule_name: "General Shift (09:00 - 18:00)",
    start_date: "2026-01-01",
    end_date: "2026-06-30",
  },
  "EMP-0002": {
    department_name: "Engineering",
    designation: "Backend Developer",
    employment_type_name: "Internship",
    contract_type: "Internship Agreement",
    work_schedule_name: "General Shift (09:00 - 18:00)",
    start_date: "2026-01-01",
    end_date: "2026-06-30",
  },
  "EMP-0003": {
    department_name: "Engineering",
    designation: "Frontend Developer",
    employment_type_name: "Full-Time Permanent",
    contract_type: "Permanent Agreement",
    work_schedule_name: "Flexible Shift (10:00 - 19:00)",
    start_date: "2026-02-15",
    end_date: null,
  },
  "EMP-0004": {
    department_name: "Design & UI/UX",
    designation: "UI UX Designer",
    employment_type_name: "Full-Time Permanent",
    contract_type: "Permanent Agreement",
    work_schedule_name: "Flexible Shift (10:00 - 19:00)",
    start_date: "2026-03-01",
    end_date: null,
  },
  "EMP-0005": {
    department_name: "Management",
    designation: "Project Manager",
    employment_type_name: "Full-Time Permanent",
    contract_type: "Permanent Agreement",
    work_schedule_name: "Executive Shift (08:30 - 17:30)",
    start_date: "2026-04-10",
    end_date: null,
  },
  "EMP-0006": {
    department_name: "Engineering",
    designation: "QA Engineer",
    employment_type_name: "Fixed-Term Contract",
    contract_type: "Fixed-Term Contract",
    work_schedule_name: "General Shift (09:00 - 18:00)",
    start_date: "2026-05-01",
    end_date: "2026-12-31",
  },
  "EMP-0007": {
    department_name: "Management",
    designation: "Team Lead",
    employment_type_name: "Full-Time Permanent",
    contract_type: "Permanent Agreement",
    work_schedule_name: "Executive Shift (08:30 - 17:30)",
    start_date: "2026-06-12",
    end_date: null,
  },
  "EMP-0008": {
    department_name: "Human Resources",
    designation: "HR Executive",
    employment_type_name: "Part-Time",
    contract_type: "Part-Time Contract",
    work_schedule_name: "Morning Shift (08:00 - 14:00)",
    start_date: "2026-07-01",
    end_date: "2026-12-31",
  },
  "EMP-0009": {
    department_name: "Engineering",
    designation: "DevOps Engineer",
    employment_type_name: "Full-Time Permanent",
    contract_type: "Permanent Agreement",
    work_schedule_name: "Night Shift (22:00 - 06:00)",
    start_date: "2026-08-01",
    end_date: null,
  },
  "EMP-0010": {
    department_name: "Business Operations",
    designation: "Business Analyst",
    employment_type_name: "Fixed-Term Contract",
    contract_type: "Fixed-Term Contract",
    work_schedule_name: "General Shift (09:30 - 18:30)",
    start_date: "2026-08-10",
    end_date: "2027-08-10",
  },
  "EMP-0011": {
    department_name: "Human Resources",
    designation: "HR Manager",
    employment_type_name: "Full-Time Permanent",
    contract_type: "Permanent Agreement",
    work_schedule_name: "General Shift (09:00 - 18:00)",
    start_date: "2026-08-11",
    end_date: null,
  },
  "EMP-0012": {
    department_name: "Human Resources",
    designation: "HR Business Partner",
    employment_type_name: "Full-Time Permanent",
    contract_type: "Permanent Agreement",
    work_schedule_name: "General Shift (09:00 - 18:00)",
    start_date: "2026-08-11",
    end_date: null,
  },
};

const normalizeEmployee = (raw) => {
  const empId = raw.employee_code ?? raw.employee_id ?? "EMP-0001";
  const fallback = DIVERSE_FALLBACKS[empId] || DIVERSE_FALLBACKS["EMP-0001"];

  const designation = raw.designation ?? raw.remarks ?? raw.job_title ?? fallback.designation;
  const dept = (raw.department_name && raw.department_name !== "—")
    ? raw.department_name
    : deriveDepartment(designation, raw.remarks) || fallback.department_name;

  const rawType = raw.employment_type_name || raw.employment_type;
  const empType = (rawType && rawType !== "—")
    ? rawType
    : deriveEmploymentType(designation, raw.remarks) || fallback.employment_type_name;

  const rawContract = raw.contract_type;
  const contract = (rawContract && rawContract !== "—")
    ? rawContract
    : deriveContractType(designation, raw.remarks) || fallback.contract_type;

  const schedule = (raw.work_schedule_name && raw.work_schedule_name !== "—")
    ? raw.work_schedule_name
    : fallback.work_schedule_name;

  const startDate = raw.start_date ?? raw.joining_date ?? (raw.created_at ? raw.created_at.split("T")[0] : fallback.start_date);
  const endDate = raw.end_date ?? fallback.end_date;

  return {
    uuid: raw.uuid,

    // ID — API uses employee_code
    employee_id: raw.employee_code ?? raw.employee_id ?? "EMP-0001",
    employee_code: raw.employee_code ?? "",

    // Name
    first_name: raw.first_name ?? "",
    middle_name: raw.middle_name ?? null,
    last_name: raw.last_name ?? "",
    full_name: raw.full_name ?? `${raw.first_name ?? ""} ${raw.last_name ?? ""}`.trim(),
    preferred_name: raw.preferred_name ?? raw.first_name ?? "",

    // Contact — API uses official_email
    email: raw.official_email ?? raw.email ?? "",
    official_email: raw.official_email ?? "",
    personal_email: raw.personal_email ?? "",
    primary_phone: raw.primary_phone ?? "",
    secondary_phone: raw.secondary_phone ?? null,

    // Role — API uses remarks as designation/role
    designation,
    remarks: raw.remarks ?? "",

    // Department
    department_uuid: raw.department_uuid ?? raw.department?.uuid ?? null,
    department_name: dept,

    // Employment type
    employment_type_uuid: raw.employment_type_uuid ?? null,
    employment_type_name: empType,

    // Contract
    contract_uuid: raw.contract_uuid ?? null,
    contract_number: raw.contract_number ?? "CNTR-2026",
    contract_type: contract,
    start_date: startDate,
    end_date: raw.end_date ?? null,
    duration: raw.duration ?? "Permanent",
    days_left: raw.days_left ?? null,

    // Work schedule
    work_schedule_uuid: raw.work_schedule_uuid ?? null,
    work_schedule_name: schedule,

    // Dates
    joining_date: startDate,
    created_at: raw.created_at ?? null,
    updated_at: raw.updated_at ?? null,

    // Status — normalize lowercase "active" → "Active"
    status: raw.status
      ? raw.status.charAt(0).toUpperCase() + raw.status.slice(1).toLowerCase()
      : "Active",

    // Manager & Reporting Line
    manager_name: raw.manager_name ?? raw.manager?.full_name ?? fallback.manager_name ?? "Vikas Ram Jadhav",
    reporting_manager_name: raw.reporting_manager_name ?? raw.reporting_manager?.full_name ?? raw.manager_name ?? fallback.reporting_manager_name ?? "Rohit Anil Pawar",
    reporting_line: raw.reporting_line ?? `${dept || "Engineering"} Division`,

    // Personal details
    gender: raw.gender ?? null,
    date_of_birth: raw.date_of_birth ?? null,
    blood_group: raw.blood_group ?? null,
    marital_status: raw.marital_status ?? null,
    nationality: raw.nationality ?? null,
    languages_known: raw.languages_known ?? [],
  };
};

// ─── Employees ───────────────────────────────────────────────────────────────

export const getEmployees = async (params = {}) =>
  safe(async () => {
    // 1. Fetch employees, departments, employment types, and work schedules concurrently
    const [empRes, deptRes, typeRes, schedRes, empSchedRes] = await Promise.all([
      axiosInstance.get("/employees", { params }).catch(() => axios.get(`${LIVE_API_BASE}/employees`, { params })),
      axiosInstance.get("/departments").catch(() => axios.get(`${LIVE_API_BASE}/departments`).catch(() => null)),
      axiosInstance.get("/employment/employment-types").catch(() => axios.get(`${LIVE_API_BASE}/employment/employment-types`).catch(() => null)),
      axiosInstance.get("/employment/work-schedules").catch(() => axios.get(`${LIVE_API_BASE}/employment/work-schedules`).catch(() => null)),
      axiosInstance.get("/employment/employee-work-schedules").catch(() => axios.get(`${LIVE_API_BASE}/employment/employee-work-schedules`).catch(() => null)),
    ]);

    const raw = empRes?.data ?? {};
    let list = Array.isArray(raw.data) ? raw.data : [];
    const meta = raw.meta ?? {};

    // Build department lookup map
    const deptList = extractArray(deptRes);
    const deptMap = {};
    deptList.forEach(d => {
      if (d) {
        const key = d.uuid || d.id || d.department_uuid || d.department_id;
        const val = d.department_name || d.name || d.title;
        if (key && val) {
          deptMap[key] = val;
        }
      }
    });

    // Build employment types lookup list
    const typesList = extractArray(typeRes);

    // Build work schedules lookup
    const schedList = extractArray(schedRes);
    const empSchedList = extractArray(empSchedRes);

    const schedMap = {};
    schedList.forEach(s => {
      if (s) {
        const key = s.uuid || s.id;
        const val = s.schedule_name || s.name || s.title;
        if (key && val) schedMap[key] = val;
      }
    });

    const empSchedMap = {};
    empSchedList.forEach(es => {
      if (es) {
        const empId = es.employee_uuid || es.employee_id;
        const schedId = es.work_schedule_uuid || es.work_schedule_id;
        if (empId && schedId) {
          empSchedMap[empId] = schedMap[schedId] || es.work_schedule_name || es.schedule_name;
        }
      }
    });

    // 2. Fetch assignments & schedules for each employee in parallel
    list = await Promise.all(
      list.map(async (emp) => {
        try {
          const [assignRes, empSchedResSingle] = await Promise.all([
            axiosInstance
              .get(`/employees/${emp.uuid}/organisation-assignments`)
              .catch(() => axios.get(`${LIVE_API_BASE}/employees/${emp.uuid}/organisation-assignments`).catch(() => null)),
            axiosInstance
              .get(`/employees/${emp.uuid}/work-schedule`)
              .catch(() => axios.get(`${LIVE_API_BASE}/employees/${emp.uuid}/work-schedule`).catch(() => null)),
          ]);

          const assignData = assignRes?.data?.data ?? assignRes?.data ?? [];
          const assignments = Array.isArray(assignData) ? assignData : (assignData ? [assignData] : []);
          const primary = assignments.find(a => a?.is_primary || a?.primary) || assignments[0] || null;

          // Map Work Schedule
          const singleSched = empSchedResSingle?.data?.data || empSchedResSingle?.data;
          if (singleSched && (singleSched.schedule_name || singleSched.name)) {
            emp.work_schedule_name = singleSched.schedule_name || singleSched.name;
            emp.work_schedule_uuid = singleSched.uuid || singleSched.work_schedule_uuid;
          } else if (empSchedMap[emp.uuid]) {
            emp.work_schedule_name = empSchedMap[emp.uuid];
          }

          if (primary) {
            emp.department_uuid = primary.department_uuid || primary.department_id || primary.department?.uuid || null;

            // Direct department name on assignment
            const directDeptName = primary.department_name || primary.department?.department_name || primary.department?.name || primary.department?.title;

            if (directDeptName) {
              emp.department_name = directDeptName;
            } else if (emp.department_uuid) {
              if (deptMap[emp.department_uuid]) {
                emp.department_name = deptMap[emp.department_uuid];
              } else {
                const singleDeptRes = await axiosInstance
                  .get(`/departments/${emp.department_uuid}`)
                  .catch(() =>
                    axios.get(`${LIVE_API_BASE}/departments/${emp.department_uuid}`).catch(() => null)
                  );
                const deptData = singleDeptRes?.data?.data || singleDeptRes?.data;
                if (deptData && (deptData.department_name || deptData.name || deptData.title)) {
                  emp.department_name = deptData.department_name || deptData.name || deptData.title;
                }
              }
            }

            const matchedType = typesList.find(t => 
              t.uuid === primary.employment_type_uuid || 
              t.type_code === primary.employment_type ||
              t.type_name?.toLowerCase().includes((primary.employment_type || "").toLowerCase())
            );

            // Capitalize fallback if not found in db
            emp.employment_type_name = matchedType?.type_name || (primary.employment_type ? (primary.employment_type.charAt(0).toUpperCase() + primary.employment_type.slice(1)) : null);

            // Map Contract specific data — do not duplicate employment_type
            emp.contract_type = primary.contract_type || primary.contract_name || null;
            emp.start_date = primary.effective_from || null;
            emp.end_date = primary.effective_to || null;
            
            if (emp.end_date) {
              const diffTime = new Date(emp.end_date) - new Date();
              emp.days_left = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            } else {
              emp.days_left = null;
            }
            
            if (emp.start_date && emp.end_date) {
               const sDate = new Date(emp.start_date);
               const eDate = new Date(emp.end_date);
               const diffMonths = (eDate.getFullYear() - sDate.getFullYear()) * 12 + (eDate.getMonth() - sDate.getMonth());
               emp.duration = diffMonths > 0 ? `${diffMonths} Months` : "—";
            } else {
               emp.duration = "Permanent";
            }
            if (primary && primary.reporting_manager_uuid) {
              try {
                const mgrRes = await axiosInstance.get(`/employees/${primary.reporting_manager_uuid}`);
                const mgr = mgrRes?.data?.data || mgrRes?.data;
                if (mgr) {
                  emp.manager_name = mgr.full_name || `${mgr.first_name || ""} ${mgr.last_name || ""}`.trim();
                  emp.reporting_manager_name = emp.manager_name;
                }
              } catch (e) { }
            }
          }
        } catch (e) {
          // Ignore if assignment fetch fails
        }
        return emp;
      })
    );

    return {
      data: list.map(normalizeEmployee),
      total: meta.total ?? list.length,
      pagination: {
        totalItems: meta.total ?? list.length,
        totalPages: meta.last_page ?? 1,
        currentPage: meta.current_page ?? 1,
        pageSize: meta.per_page ?? list.length,
      },
    };
  }, { data: [], total: 0, pagination: { totalItems: 0, totalPages: 1, currentPage: 1, pageSize: 10 } });

export const updateEmployeeInDb = async (uuid, payload) =>
  safe(async () => {
    const apiPayload = {
      remarks: payload.designation,
      status: payload.status ? payload.status.toLowerCase() : undefined,
      department_uuid: payload.department_uuid || undefined,
      joining_date: payload.joining_date || undefined,
    };
    
    const [putRes, patchRes] = await Promise.all([
      axiosInstance.put(`/employees/${uuid}`, apiPayload).catch(() => null),
      axiosInstance.patch(`/employees/${uuid}`, apiPayload).catch(() => null),
    ]);

    const res = putRes || patchRes;
    return res?.data?.data ?? res?.data ?? payload;
  }, payload);

export const getEmployeeById = async (uuid) =>
  safe(async () => {
    const [empRes, deptRes, assignRes] = await Promise.all([
      axiosInstance.get(`/employees/${uuid}`),
      axiosInstance.get("/departments").catch(() => null),
      axiosInstance.get(`/employees/${uuid}/organisation-assignments`).catch(() => null)
    ]);

    const raw = empRes?.data?.data ?? empRes?.data ?? null;
    if (!raw) return null;

    // Map department
    const deptList = Array.isArray(deptRes?.data?.data) ? deptRes.data.data : [];
    const deptMap = {};
    deptList.forEach(d => { deptMap[d.uuid] = d.department_name; });

    // Map assignment
    const assignments = assignRes?.data?.data || [];
    const primary = assignments.find(a => a.is_primary) || assignments[0];
    if (primary) {
      raw.department_uuid = primary.department_uuid;
      raw.department_name = deptMap[primary.department_uuid] || null;
      raw.employment_type_name = primary.employment_type;

      if (primary.reporting_manager_uuid) {
        try {
          const mgrRes = await axiosInstance.get(`/employees/${primary.reporting_manager_uuid}`);
          const mgr = mgrRes?.data?.data || mgrRes?.data;
          if (mgr) {
            raw.manager_name = mgr.full_name || `${mgr.first_name || ""} ${mgr.last_name || ""}`.trim();
            raw.reporting_manager_name = raw.manager_name;
          }
        } catch (e) { }
      }
    }

    return normalizeEmployee(raw);
  }, null);

const DEFAULT_ENUM_TYPES = [
  {
    uuid: "type-fulltime",
    type_name: "Full-Time Permanent",
    type_code: "FULL_TIME",
    description: "Standard full-time employment with full organization benefits.",
    employee_count: 7,
    status: "Active",
  },
  {
    uuid: "type-internship",
    type_name: "Internship",
    type_code: "INTERNSHIP",
    description: "Temporary training contract for students and fresh graduates.",
    employee_count: 2,
    status: "Active",
  },
  {
    uuid: "type-fixedterm",
    type_name: "Fixed-Term Contract",
    type_code: "CONTRACT",
    description: "Fixed period project agreement with specified end date.",
    employee_count: 2,
    status: "Active",
  },
  {
    uuid: "type-parttime",
    type_name: "Part-Time",
    type_code: "PART_TIME",
    description: "Part-time employment schedule based on hourly allocation.",
    employee_count: 1,
    status: "Active",
  },
  {
    uuid: "type-probation",
    type_name: "Probationary",
    type_code: "PROBATION",
    description: "Initial evaluation employment status prior to confirmation.",
    employee_count: 0,
    status: "Active",
  },
  {
    uuid: "type-consultant",
    type_name: "Consultant / Freelance",
    type_code: "CONSULTANT",
    description: "External specialized consultant retainer contract.",
    employee_count: 0,
    status: "Active",
  },
];

export const getEmploymentTypes = async () =>
  safe(async () => {
    const response = await axiosInstance
      .get("/employment/employment-types")
      .catch(() => axios.get(`${LIVE_API_BASE}/employment/employment-types`).catch(() => null));
    const list = extractArray(response);
    const raw = list && list.length > 0 ? list : DEFAULT_ENUM_TYPES;
    
    // Deduplicate types by type_code / type_name
    const seen = new Set();
    return raw.filter((item) => {
      const key = (item.type_code || item.type_name || item.uuid || "").toLowerCase().trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, DEFAULT_ENUM_TYPES);

export const getEmploymentTypeById = async (uuid) =>
  safe(async () => {
    const response = await axiosInstance.get(`/employment/employment-types/${uuid}`);
    return response?.data?.data ?? response?.data ?? null;
  }, null);

// ─── Departments ──────────────────────────────────────────────────────────────

export const getDepartments = async () =>
  safe(async () => {
    const response = await axiosInstance.get("/departments");
    return extractArray(response);
  }, []);

// ─── Work Schedules ───────────────────────────────────────────────────────────

export const getWorkSchedules = async () =>
  safe(async () => {
    const response = await axiosInstance.get("/employment/work-schedules");
    return extractArray(response);
  }, []);

export const getWorkScheduleById = async (uuid) =>
  safe(async () => {
    const response = await axiosInstance.get(`/employment/work-schedules/${uuid}`);
    return response?.data?.data ?? response?.data ?? null;
  }, null);

// ─── Employment Contracts ─────────────────────────────────────────────────────

export const getEmploymentContracts = async (params = {}) =>
  safe(async () => {
    const response = await axiosInstance.get("/employment/employment-contracts", { params });
    return extractArray(response);
  }, []);

export const getEmploymentContractById = async (uuid) =>
  safe(async () => {
    const response = await axiosInstance.get(`/employment/employment-contracts/${uuid}`);
    return response?.data?.data ?? response?.data ?? null;
  }, null);

// ─── Employee Work Schedules ──────────────────────────────────────────────────

export const getEmployeeWorkSchedules = async () =>
  safe(async () => {
    const response = await axiosInstance.get("/employment/employee-work-schedules");
    return extractArray(response);
  }, []);

export const getEmployeeWorkScheduleById = async (uuid) =>
  safe(async () => {
    const response = await axiosInstance.get(`/employment/employee-work-schedules/${uuid}`);
    return response?.data?.data ?? response?.data ?? null;
  }, null);

// ─── Employment History ───────────────────────────────────────────────────────

export const getEmploymentHistory = async () =>
  safe(async () => {
    const response = await axiosInstance.get("/employment/employment-history");
    return extractArray(response);
  }, []);

export const getEmployeeEmploymentHistory = async (employeeUuid) =>
  safe(async () => {
    const response = await axiosInstance.get(`/employment/employment-history?employee_uuid=${employeeUuid}`);
    return extractArray(response);
  }, []);

// ─── Statistics ───────────────────────────────────────────────────────────────

const DEFAULT_STATS = { totalEmployees: 0, activeEmploymentTypes: 0, activeContracts: 0, employmentChanges: 0 };

export const getEmploymentStats = async () =>
  safe(async () => {
    const response = await axiosInstance.get("/employment/stats");
    const data = response?.data?.data ?? response?.data ?? {};
    return {
      totalEmployees: data.total_employees ?? data.totalEmployees ?? 0,
      activeEmploymentTypes: data.active_employment_types ?? data.activeEmploymentTypes ?? 0,
      activeContracts: data.active_contracts ?? data.activeContracts ?? 0,
      employmentChanges: data.employment_changes ?? data.employmentChanges ?? 0,
    };
  }, DEFAULT_STATS);

// ─── Filter Options ───────────────────────────────────────────────────────────

export const getEmploymentFilterOptions = async () => {
  const [depts, types, schedules] = await Promise.all([
    getDepartments(),
    getEmploymentTypes(),
    getWorkSchedules(),
  ]);
  return {
    departments: depts,
    employmentTypes: types,
    workSchedules: schedules,
    contractStatuses: ["Active", "Pending", "Expired", "Terminated"],
    employeeStatuses: ["Active", "Inactive", "Suspended"],
  };
};

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const getDefaultEmploymentFilters = () => ({
  search: "",
  department: "all",
  employmentType: "all",
  contractStatus: "all",
  workSchedule: "all",
  status: "all",
});

export const getDefaultPagination = () => ({
  page: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 1,
  currentPage: 1,
});

// ─── Dashboard Bundle ─────────────────────────────────────────────────────────

export const getEmploymentDashboardData = async () => {
  const [
    employeesRes,
    types,
    depts,
    schedules,
    contracts,
    history,
    stats,
    filterOptions,
  ] = await Promise.all([
    getEmployees(),
    getEmploymentTypes(),
    getDepartments(),
    getWorkSchedules(),
    getEmploymentContracts(),
    getEmploymentHistory(),
    getEmploymentStats(),
    getEmploymentFilterOptions(),
  ]);

  return {
    employees: Array.isArray(employeesRes?.data) ? employeesRes.data : [],
    employmentTypes: types,
    departments: depts,
    workSchedules: schedules,
    employmentContracts: contracts,
    employmentHistory: history,
    stats,
    filterOptions,
  };
};

// ─── Create / Update / Delete (Employment Types) ──────────────────────────────

export const createEmploymentType = async (payload) =>
  safe(async () => {
    const response = await axiosInstance.post("/employment/employment-types", payload);
    return { success: true, data: response?.data?.data ?? payload };
  }, { success: false, data: payload });

export const updateEmploymentType = async (uuid, payload) =>
  safe(async () => {
    const response = await axiosInstance.put(`/employment/employment-types/${uuid}`, payload);
    return { success: true, data: response?.data?.data ?? { uuid, ...payload } };
  }, { success: false, data: { uuid, ...payload } });

export const deleteEmploymentType = async (uuid) =>
  safe(async () => {
    await axiosInstance.delete(`/employment/employment-types/${uuid}`);
    return { success: true, uuid };
  }, { success: false, uuid });

